import React from 'react'
import { Row, Col, Card, Statistic, Table, Tag, Progress, Typography, message } from 'antd'
import {
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
} from '@ant-design/icons'

const { Title } = Typography

// 模拟数据
const statisticsData = {
  total_tasks: 156,
  completed_tasks: 98,
  pending_tasks: 32,
  in_progress_tasks: 18,
  blocked_tasks: 8,
  completion_rate: 62.8,
  platform_distribution: {
    haul: 45,
    walmart: 38,
    joom: 28,
    ozon: 22,
    shopify: 15,
  },
  type_distribution: {
    listing: 35,
    operation: 42,
    seo: 28,
    data: 18,
    development: 15,
  },
  priority_distribution: {
    urgent: 8,
    high: 42,
    medium: 68,
    low: 38,
  },}

const recentTasks = [
  {
    key: '1',
    title: 'Haul 家居店上架 10 个商品',
    platform: 'haul',
    status: 'completed',
    priority: 'high',
    assignee: '张三',
    due_date: '2026-09-15',
  },
  {
    key: '2',
    title: 'Walmart 质量分优化',
    platform: 'walmart',
    status: 'in_progress',
    priority: 'medium',
    assignee: '李四',
    due_date: '2026-09-20',
  },
  {
    key: '3',
    title: 'Joom 促销活动报名',
    platform: 'joom',
    status: 'pending',
    priority: 'high',
    assignee: '王五',
    due_date: '2026-09-18',
  },
  {
    key: '4',
    title: 'Ozon 广告投放优化',
    platform: 'ozon',
    status: 'blocked',
    priority: 'urgent',
    assignee: '赵六',
    due_date: '2026-09-12',
  },
  {
    key: '5',
    title: 'Shopify SEO 优化',
    platform: 'shopify',
    status: 'in_progress',
    priority: 'low',
    assignee: '钱七',
    due_date: '2026-09-25',
  },
]

const platformColors: Record<string, string> = {
  haul: '#f50',
  walmart: '#1890ff',
  joom: '#52c41a',
  ozon: '#722ed1',
  shopify: '#13c2c2',
  other: '#8c8c8c',
}

const statusColors: Record<string, string> = {
  completed: 'success',
  in_progress: 'processing',
  pending: 'warning',
  blocked: 'error',
  cancelled: 'default',
}

const priorityColors: Record<string, string> = {
  low: 'green',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
}

const columns = [
  {
    title: '任务名称',
    dataIndex: 'title',
    key: 'title',
    width: 300,
  },
  {
    title: '平台',
    dataIndex: 'platform',
    key: 'platform',
    width: 100,
    render: (platform: string) => (
      <Tag color={platformColors[platform]}>
        {platform.toUpperCase()}
      </Tag>
    ),
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status: string) => (
      <Tag color={statusColors[status]}>
        {status === 'completed' ? '已完成' :
         status === 'in_progress' ? '进行中' :
         status === 'pending' ? '待办' :
         status === 'blocked' ? '阻塞' : '已取消'}
      </Tag>
    ),
  },
  {
    title: '优先级',
    dataIndex: 'priority',
    key: 'priority',
    width: 100,
    render: (priority: string) => (
      <Tag color={priorityColors[priority]}>
        {priority === 'low' ? '低' :
         priority === 'medium' ? '中' :
         priority === 'high' ? '高' : '紧急'}
      </Tag>
    ),
  },
  {
    title: '负责人',
    dataIndex: 'assignee',
    key: 'assignee',
    width: 100,
  },
  {
    title: '截止日期',
    dataIndex: 'due_date',
    key: 'due_date',
    width: 120,
  },
]

const Dashboard: React.FC = () => {
  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        仪表盘
      </Title>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总任务数"
              value={statisticsData.total_tasks}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已完成"
              value={statisticsData.completed_tasks}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="进行中"
              value={statisticsData.in_progress_tasks}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="阻塞中"
              value={statisticsData.blocked_tasks}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 完成率 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="任务完成率">
            <Progress
              type="circle"
              percent={statisticsData.completion_rate}
              format={(percent) => `${percent}%`}
              size={200}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="平台分布">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {Object.entries(statisticsData.platform_distribution || {}).map(([platform, count]) => (
                <Card key={platform} size="small" style={{ minWidth: 120 }}>
                  <Statistic
                    title={platform.toUpperCase()}
                    value={count}
                    prefix={<ShopOutlined />}
                    valueStyle={{ color: platformColors[platform] || '#8c8c8c' }}
                  />
                </Card>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 最近任务 */}
      <Card title="最近任务">
        <Table
          columns={columns}
          dataSource={recentTasks}
          pagination={false}
          size="middle"
        />
      </Card>
    </div>
  )
}

export default Dashboard
